import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  People as PeopleIcon,
  EditCalendar as EditCalendarIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Description as DescriptionIcon,
  LibraryBooks as LibraryBooksIcon,
  AccountBalanceWallet as SalaryIcon,
  TrendingUp as ProfitIcon,
  Percent as MarginIcon,
  Settings as SettingsIcon,
  AutoAwesome as AutoAwesomeIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  Engineering as EngineeringIcon,
  SwapHoriz as SwapHorizIcon,
  Contacts as ContactsIcon,
} from '@mui/icons-material';
import { logout, getCurrentUser } from '../api';
import PageTransition from './PageTransition';

const drawerWidth = 240;

// 桌面端菜单分组
const menuGroups = [
  {
    label: '概览',
    items: [
      { text: '首页', icon: <HomeIcon />, path: '/' },
    ],
  },
  {
    label: '业务管理',
    items: [
      { text: '项目', icon: <WorkIcon />, path: '/projects' },
      { text: '工人', icon: <PeopleIcon />, path: '/workers' },
      { text: '记工', icon: <EditCalendarIcon />, path: '/work-logs' },
      { text: '材料', icon: <InventoryIcon />, path: '/materials' },
      { text: '杂费', icon: <ReceiptIcon />, path: '/expenses' },
      { text: '收款', icon: <PaymentIcon />, path: '/incomes' },
      { text: '借支/扣款', icon: <SwapHorizIcon />, path: '/adjustments' },
      { text: '通讯录', icon: <ContactsIcon />, path: '/contacts' },
    ],
  },
  {
    label: '报价与报表',
    items: [
      { text: '报价单', icon: <DescriptionIcon />, path: '/quotations' },
      { text: '单价库', icon: <LibraryBooksIcon />, path: '/quota-library' },
      { text: '报价净利率', icon: <MarginIcon />, path: '/quotation-margin' },
      { text: '工资', icon: <SalaryIcon />, path: '/salary' },
      { text: '利润', icon: <ProfitIcon />, path: '/profit' },
    ],
  },
  {
    label: '系统',
    items: [
      { text: '设置', icon: <SettingsIcon />, path: '/settings' },
      { text: 'AI 设置', icon: <AutoAwesomeIcon />, path: '/ai-settings' },
    ],
  },
];

// 移动端底部导航（主要功能）
const bottomNavItems = [
  { label: '首页', icon: <HomeIcon />, path: '/' },
  { label: '项目', icon: <WorkIcon />, path: '/projects' },
  { label: '记工', icon: <EditCalendarIcon />, path: '/work-logs' },
  { label: '报价', icon: <DescriptionIcon />, path: '/quotations' },
  { label: '设置', icon: <SettingsIcon />, path: '/settings' },
];

export default function Layout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // 当前底部导航索引
  const currentBottomNavIndex = bottomNavItems.findIndex(
    (item) => item.path === location.pathname
  );

  const drawer = (
    <div>
      {/* 品牌Logo区域 */}
      <Toolbar
        sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EngineeringIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              施工记账管家
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
              Construction Manager
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider />
      {/* 分组菜单 */}
      <List sx={{ px: 1, py: 1 }}>
        {menuGroups.map((group, groupIndex) => (
          <React.Fragment key={group.label}>
            {/* 分组标题 */}
            <ListSubheader
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: 1.5,
                px: 2,
                py: 1.5,
                mt: groupIndex > 0 ? 1 : 0,
              }}
            >
              {group.label}
            </ListSubheader>
            {/* 菜单项 */}
            {group.items.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }}
                  sx={{
                    mb: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      color: 'primary.dark',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '25%',
                        bottom: '25%',
                        width: 3,
                        borderRadius: '0 4px 4px 0',
                        backgroundColor: 'primary.main',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: location.pathname === item.path ? 'primary.dark' : 'inherit',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {/* 分组分割线 */}
            {groupIndex < menuGroups.length - 1 && (
              <Divider sx={{ my: 1, mx: 2 }} />
            )}
          </React.Fragment>
        ))}
      </List>
      <Divider />
      {/* 登出按钮 */}
      <List sx={{ px: 1, py: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={logout}
            sx={{
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'error.dark',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="退出登录" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* 顶部导航栏 */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuGroups.flatMap((group) => group.items).find((item) => item.path === location.pathname)?.text || '施工记账管家'}
          </Typography>
          {/* 右上角用户信息 + 登出 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountIcon />
            <Typography variant="body2" noWrap>
              {(getCurrentUser()?.username) || '用户'}
            </Typography>
            <IconButton color="inherit" onClick={logout} title="退出登录">
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 侧边栏（桌面端） */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* 移动端临时抽屉 */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        {/* 桌面端固定抽屉 */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* 主内容区 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          mb: isMobile ? '56px' : 0, // 移动端为底部导航留空间
          pb: isMobile ? 6 : 3, // padding 兜底：内容贴底元素（如对比按钮）不被 fixed 底导拦截点击
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <PageTransition>
          {children}
        </PageTransition>
      </Box>

      {/* 底部导航栏（移动端） */}
      {isMobile && (
        <BottomNavigation
          value={currentBottomNavIndex >= 0 ? currentBottomNavIndex : 0}
          onChange={(event, newValue) => {
            navigate(bottomNavItems[newValue].path);
          }}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          {bottomNavItems.map((item) => (
            <BottomNavigationAction
              key={item.label}
              label={item.label}
              icon={item.icon}
              sx={{
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              }}
            />
          ))}
        </BottomNavigation>
      )}
    </Box>
  );
}
